import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, ArrowRight, TrendingUp, Users, Award } from "lucide-react";
import anniversaryImage from "@/assets/natcon-10th-anniversary.jpg";

const LatestNews = () => {
  const news = [
    {
      id: 1,
      title: "NATCON SACCO 10th Anniversary Celebration",
      excerpt: "Join us in celebrating a decade of growth! From Kshs. 200,000 to over Kshs. 100 million in member savings. December 5, 2025 at Radisson Blu Hotel.",
      date: "2025-12-05",
      category: "Milestone Event",
      icon: Calendar,
      featured: true,
      image: anniversaryImage
    },
    {
      id: 2,
      title: "Government Reforms Set to Strengthen SACCO Sector Accountability and Growth",
      excerpt: "The Kenyan government has embarked on a critical review of the SACCO Societies Act 2008, signaling a new era of accountability, transparency, and robust governance in the SACCO sector.",
      date: "2025-08-12",
      category: "Policy & Regulation",
      icon: TrendingUp,
      featured: false
    },
    {
      id: 3,
      title: "SACCO Sector Growth Hits New Highs with Increased Member Returns and Asset Growth",
      excerpt: "Kenya's SACCO sector is experiencing remarkable growth, demonstrating its vital role as a pillar of the country's financial system with 10% increase in total assets.",
      date: "2025-08-10",
      category: "Industry Insights",
      icon: TrendingUp,
      featured: false
    },
    {
      id: 4,
      title: "Upcoming SACCO Sector Conference 2025 to Focus on Challenges and Opportunities",
      excerpt: "From August 20 to 22, 2025, the vibrant city of Kisumu will host the much-anticipated SACCO Sector Conference 2025.",
      date: "2025-08-08",
      category: "Events & Conferences",
      icon: Calendar,
      featured: false
    }
  ];

  const getCategoryColor = (category: string) => {
    switch (category) {
      case "Milestone Event": return "bg-primary text-primary-foreground";
      case "Policy & Regulation": return "bg-trust-green text-white";
      case "Industry Insights": return "bg-trust-blue text-white";
      case "Events & Conferences": return "bg-accent text-accent-foreground";
      case "Saving Tips": return "bg-warning-orange text-white";
      default: return "bg-secondary text-secondary-foreground";
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <section className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Latest News & Updates
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Stay informed with the latest developments, achievements, and opportunities at Natcon Sacco
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Featured Article */}
          {news.filter(item => item.featured).map((item) => {
            const Icon = item.icon;
            return (
              <Card key={item.id} className="lg:col-span-2 border-primary/20 shadow-soft hover:shadow-medium transition-all duration-300 overflow-hidden">
                {item.image && (
                  <div className="w-full h-64 lg:h-80 overflow-hidden">
                    <img 
                      src={item.image} 
                      alt={item.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                )}
                <CardHeader>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className={`h-12 w-12 rounded-lg ${getCategoryColor(item.category)} flex items-center justify-center`}>
                        <Icon className="h-6 w-6" />
                      </div>
                      <div>
                        <Badge className={getCategoryColor(item.category)} variant="secondary">
                          {item.category}
                        </Badge>
                        <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
                          <Calendar className="h-4 w-4" />
                          {formatDate(item.date)}
                        </div>
                      </div>
                    </div>
                    <Badge variant="secondary" className="bg-primary text-primary-foreground">
                      Featured
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardTitle className="text-xl mb-3 leading-tight">
                    {item.title}
                  </CardTitle>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    {item.excerpt}
                  </p>
                  <Button variant="outline" className="group" asChild>
                    <a href="/news-media">
                      Read Full Article
                      <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </a>
                  </Button>
                </CardContent>
              </Card>
            );
          })}

          {/* Regular Articles */}
          {news.filter(item => !item.featured).map((item) => {
            const Icon = item.icon;
            return (
              <Card key={item.id} className="border-border/50 hover:border-primary/20 shadow-soft hover:shadow-medium transition-all duration-300">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-3">
                    <div className={`h-10 w-10 rounded-lg ${getCategoryColor(item.category)} flex items-center justify-center`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1">
                      <Badge className={getCategoryColor(item.category)} variant="secondary">
                        {item.category}
                      </Badge>
                      <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                        <Calendar className="h-3 w-3" />
                        {formatDate(item.date)}
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <CardTitle className="text-lg mb-2 leading-tight">
                    {item.title}
                  </CardTitle>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                    {item.excerpt}
                  </p>
                  <Button variant="ghost" size="sm" className="p-0 h-auto text-primary hover:text-primary/80" asChild>
                    <a href="/news-media">
                      Read Article
                      <ArrowRight className="h-3 w-3 ml-1" />
                    </a>
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="text-center mt-12">
          <Button size="lg" variant="outline" className="group" asChild>
            <a href="/news-media">
              View All News & Updates
              <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </a>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default LatestNews;